import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useBoolean } from 'react-use'

import { Plus16 } from '@makyoapp/makyo-icon/icon'
import { Button, ImgEasyResult } from '@makyoapp/makyo-ui'

import { lazyModal } from '@/lib/lazy-load'
import { toasted } from '@/lib/toast'
import { useAddNewTeamMutation } from '@/resolvers/org/mutation'
import { useReqError } from '@/hooks/useReqError'

const BasicInfoEditModal = lazyModal(() => import('@/components/BasicInfoEditModal'))

/**
 * add new team modal
 *
 * @param onAfterUpdate
 * @constructor
 */
function AddNewTeamModal({ onAfterUpdate, currentOrgId }: { onAfterUpdate: () => void; currentOrgId?: string }) {
  const { t } = useTranslation()

  const [showModal, setShowModal] = useBoolean(false)
  const [isLoading, setIsLoading] = useBoolean(false)
  const printAPIError = useReqError()
  const { mutate: addNewTeamMutation } = useAddNewTeamMutation()
  const navigate = useNavigate()
  /**
   * This function handles the submission of the form for adding a new team.
   *
   * @param {string} nextName - The name of the new team.
   * @param {ImgEasyResult | undefined} avatar - The avatar of the new team.
   * @param {() => void | undefined} done - Callback function that is called after the team is added.
   *
   * The function starts by setting the isLoading state to true to show loading on the form.
   * It then calls the addNewTeamMutation function with the new team's information.
   *
   * On error, the error message is printed.
   *
   * When the mutation is settled, the isLoading state is removed, the modal is closed, the done callback is called,
   * and the onAfterUpdate function is called to update the organization information.
   */
  const handleSubmit = useCallback(
    (nextName: string, avatar?: ImgEasyResult, done?: () => void) => {
      setIsLoading(true)

      addNewTeamMutation(
        { nextName, avatar },
        {
          onError(e) {
            printAPIError(e)
          },
          onSettled: async (data) => {
            await onAfterUpdate()

            setIsLoading(false)
            setShowModal(false)
            toasted('New team added!')
            navigate(`/o/${currentOrgId}/teams/${data?._id}`)
            done?.()
          },
        }
      )
    },
    [setIsLoading, addNewTeamMutation, printAPIError, onAfterUpdate, setShowModal, navigate, currentOrgId]
  )

  const handleClose = () => {
    if (isLoading) return
    setShowModal(false)
  }

  return (
    <>
      <Button onClick={() => setShowModal(true)} icon={<Plus16 />}>
        {t('Add New Team')}
      </Button>
      <BasicInfoEditModal
        id='add-new-team'
        show={showModal}
        onHide={handleClose}
        title={t('Add New Teams')}
        name={''}
        avatar={''}
        onSubmit={handleSubmit}
        isLoading={isLoading}
      />
    </>
  )
}

export default AddNewTeamModal
